import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  
  // Pro-email.app integration state
  const [proEmailToken, setProEmailToken] = useState(localStorage.getItem('proEmailToken'));
  const [targetGroupId, setTargetGroupId] = useState(localStorage.getItem('targetGroupId'));
  const [proEmailUser, setProEmailUser] = useState(null);
  const [integrationMode, setIntegrationMode] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fragmentParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
    const launchToken = fragmentParams.get('token') || urlParams.get('token');
    const externalToken = urlParams.get('token');
    const groupId = urlParams.get('groupId');

    console.log('AuthContext init - URL params:', {
      pathname,
      launchToken: !!launchToken,
      groupId,
    });

    if (pathname === '/launch' && launchToken) {
      console.log('Tool launch mode detected - exchanging launch token');
      setIntegrationMode(true);
      setProEmailToken(null);
      localStorage.removeItem('proEmailToken');
      exchangeLaunchToken(launchToken, groupId);
    } else if (externalToken) {
      // Legacy integration: long-lived Pro-Email JWT at root ?token=
      console.log('Integration mode detected - validating token');
      setIntegrationMode(true);
      setProEmailToken(externalToken);
      if (groupId) {
        setTargetGroupId(groupId);
        localStorage.setItem('targetGroupId', groupId);
      }
      validateProEmailToken(externalToken, groupId);
    } else if (token) {
      console.log('Normal mode - using stored token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      console.log('No token found - showing login');
      setLoading(false);
    }
  }, []);

  const exchangeLaunchToken = async (launchToken, fallbackGroupId = null) => {
    try {
      console.log('Exchanging tool launch token...');
      const response = await axios.post(
        `${API}/integration/exchange-launch`,
        { token: launchToken }
      );
      const { token: sessionToken, user: userData, group_id: signedGroupId } = response.data;
      const launchGroupId = signedGroupId || fallbackGroupId;

      localStorage.setItem('token', sessionToken);
      if (launchGroupId) {
        localStorage.setItem('targetGroupId', launchGroupId);
        setTargetGroupId(launchGroupId);
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${sessionToken}`;
      setToken(sessionToken);
      setUser(userData);
      setLoading(false);

      window.history.replaceState({}, document.title, '/');
    } catch (error) {
      console.error('Failed to exchange launch token:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('proEmailToken');
      localStorage.removeItem('targetGroupId');
      window.history.replaceState({}, document.title, '/');
      setLoading(false);
    }
  };

  const validateProEmailToken = async (externalToken, groupId) => {
    try {
      console.log('Validating pro-email token...');
      const response = await axios.get(`${API}/integration/validate-token?token=${externalToken}`);
      
      if (response.data.valid) {
        console.log('Token valid:', response.data);
        setProEmailUser(response.data.user);
        
        // Set the token for all API calls
        axios.defaults.headers.common['Authorization'] = `Bearer ${externalToken}`;
        setToken(externalToken);
        localStorage.setItem('token', externalToken);
        localStorage.setItem('proEmailToken', externalToken);
        
        // Create a synthetic user object for the UI
        const userData = response.data.user || {};
        const syntheticUser = {
          id: response.data.user_id,
          email: userData.email || 'pro-email-user',
          name: userData.name || 'Pro-Email User'
        };
        
        setUser(syntheticUser);
        setLoading(false);
        
        // Clean up URL params after processing
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        throw new Error('Token validation returned invalid');
      }
    } catch (error) {
      console.error('Failed to validate pro-email token:', error);
      // Clear any stored tokens
      localStorage.removeItem('token');
      localStorage.removeItem('proEmailToken');
      localStorage.removeItem('targetGroupId');
      // Show login instead of redirecting away
      setLoading(false);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Token might be invalid, clear it
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('proEmailToken');
    localStorage.removeItem('targetGroupId');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setProEmailToken(null);
    setTargetGroupId(null);
    setProEmailUser(null);
    setIntegrationMode(false);
  };

  const importToProEmail = async (contactIds = null) => {
    const storedProToken = localStorage.getItem('proEmailToken') || proEmailToken;
    const storedGroupId = localStorage.getItem('targetGroupId') || targetGroupId;
    
    if (!storedGroupId) {
      throw new Error('No pro-email.app integration active');
    }

    const legacyTokenQuery = storedProToken
      ? `?token=${encodeURIComponent(storedProToken)}`
      : '';
    
    const response = await axios.post(
      `${API}/integration/import-to-group${legacyTokenQuery}`,
      { group_id: storedGroupId, contact_ids: contactIds }
    );
    
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout,
      // Integration props
      integrationMode,
      proEmailToken,
      targetGroupId,
      proEmailUser,
      importToProEmail
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
